import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Box, 
  Typography, 
  Grow,
  Tooltip
} from '@mui/material';
import { Check } from 'lucide-react';
import { styled } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { getUserOnboardingData } from '../../AGTHall/services/AGTHallService';

// Constantes de color - NO se usa directamente en wallpaper mode
const ACCENT_COLOR = '#0385FF';

const normalizeOnboardingContext = (onboardingData) => {
  if (!onboardingData || typeof onboardingData !== 'object') {
    return null;
  }

  const goals = Array.isArray(onboardingData.goals)
    ? onboardingData.goals.filter((goal) => typeof goal === 'string' && goal.trim().length > 0)
    : [];

  const rawGoalDetail = onboardingData.goalDetail;
  const goalDetail = rawGoalDetail && typeof rawGoalDetail === 'object'
    ? {
        mode: rawGoalDetail.mode || null,
        focusAreas: Array.isArray(rawGoalDetail.focusAreas)
          ? rawGoalDetail.focusAreas.filter((area) => typeof area === 'string' && area.trim().length > 0)
          : [],
        exploration: rawGoalDetail.exploration || null,
        other: rawGoalDetail.other || null,
        otherText: typeof rawGoalDetail.otherText === 'string' ? rawGoalDetail.otherText.trim() : ''
      }
    : null;

  if (!goals.length && !goalDetail) {
    return null;
  }

  return {
    goals,
    goalDetail
  };
};

const getFallbackPromptCards = (t) => ([
  {
    text: t('promptCards.lifeosTitle', 'Configurar mi LifeOS'),
    subtext: t('promptCards.lifeosDesc', 'Idioma, tema, apps y personalidad.'),
    action: 'setup_lifeos',
    prompt: t(
      'promptCards.lifeosPrompt',
      'Quiero configurar mi LifeOS: idioma, tema, apps y personalidad. Guiame paso a paso.'
    ),
    isProOnly: false
  },
  {
    text: t('promptCards.finTitle', 'Activar mis finanzas'),
    subtext: t('promptCards.finDesc', 'Ingresos, gastos y objetivos.'),
    action: 'activate_fin',
    prompt: t(
      'promptCards.finPrompt',
      'Quiero activar mis finanzas y empezar a registrar ingresos y gastos. Ayudame a configurarlo.'
    ),
    isProOnly: false
  },
  {
    text: t('promptCards.healthTitle', 'Activar mi salud/fitness'),
    subtext: t('promptCards.healthDesc', 'Rutinas, habitos y seguimiento.'),
    action: 'activate_health_fit',
    prompt: t(
      'promptCards.healthPrompt',
      'Quiero activar mi salud/fitness para llevar rutinas y habitos. Guiame para configurarlo.'
    ),
    isProOnly: false
  },
  {
    text: t('promptCards.decideTitle', 'Ayudame a decidir'),
    subtext: t('promptCards.decideDesc', 'Opciones, pros/contras y recomendacion.'),
    action: 'help_decide',
    prompt: t(
      'promptCards.decidePrompt',
      'Necesito ayuda para decidir. Te doy el contexto y quiero pros/contras y una recomendacion.'
    ),
    isProOnly: false
  }
]);

const buildGoalCards = (context, t) => {
  if (!context.goals.length) {
    return [];
  }

  const primaryGoal = context.goals[0];
  return [
    {
      text: t('promptCards.dynamic.goal.title', 'What is occupying my mind right now'),
      subtext: t('promptCards.dynamic.goal.subtext', 'Let us ground it without overthinking.'),
      action: `onboarding_goal_${primaryGoal}`,
      prompt: t(
        'promptCards.dynamic.goal.prompt',
        'Help me unpack what is most present right now. Ask short questions to help me discover context, connect what matters, and decide the best next step.'
      )
    }
  ];
};

const buildGoalDetailCard = (context, t) => {
  const detail = context.goalDetail;
  if (!detail) {
    return null;
  }

  const modeActionMap = {
    area: 'onboarding_area_focus',
    exploration: 'onboarding_exploration_path',
    other: 'onboarding_context_order'
  };

  const basePrompt = t(
    'promptCards.dynamic.detail.prompt',
    'Let us zoom in where this shows up most for you. Help me discover what is driving it, connect priorities, and decide a first move with visible impact.'
  );
  const extraContext = detail.otherText
    ? ` ${t('promptCards.dynamic.detail.extraContext', 'Additional context: {{context}}', { context: detail.otherText })}`
    : '';

  return {
    text: t('promptCards.dynamic.detail.title', 'Where this shows up the most'),
    subtext: t('promptCards.dynamic.detail.subtext', 'Bring it down to daily reality.'),
    action: modeActionMap[detail.mode] || 'onboarding_detail_focus',
    prompt: `${basePrompt}${extraContext}`
  };
};

const dedupeCardsByAction = (cards) => {
  const seen = new Set();
  return cards.filter((card) => {
    if (!card || !card.action || seen.has(card.action)) {
      return false;
    }
    seen.add(card.action);
    return true;
  });
};

const buildAdaptivePromptCards = (t, context) => {
  const fallbackCards = getFallbackPromptCards(t);

  if (!context) {
    return fallbackCards;
  }

  const kickoffCard = {
    text: t('promptCards.dynamic.guidedStart.title', 'Where should we start'),
    subtext: t('promptCards.dynamic.guidedStart.subtext', 'Discover, connect, and decide without rushing.'),
    action: 'onboarding_guided_start',
    prompt: t(
      'promptCards.dynamic.guidedStart.prompt',
      'Guide me through a short conversation based on my onboarding. Ask concise questions so I can discover context, connect what matters, and finish with a clear first decision.'
    )
  };

  const dynamicCards = [
    kickoffCard,
    ...buildGoalCards(context, t),
    buildGoalDetailCard(context, t)
  ];

  const uniqueCards = dedupeCardsByAction(dynamicCards).slice(0, 4);
  for (const fallbackCard of fallbackCards) {
    if (uniqueCards.length >= 4) {
      break;
    }
    if (!uniqueCards.some((card) => card.action === fallbackCard.action)) {
      uniqueCards.push(fallbackCard);
    }
  }

  return uniqueCards.slice(0, 4);
};

// Estilo principal del contenedor
const PromptCardsContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '520px',
  boxSizing: 'border-box',
  padding: '8px',
  margin: '0 auto',
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '12px',
  [theme.breakpoints.down('sm')]: {
    padding: '6px',
    gridTemplateColumns: 'minmax(0, 1fr)',
  }
}));

// Nuevo estilo para cada PromptCard basado en el CategoryItem
const PromptCardItem = styled(Box, {
  shouldForwardProp: (prop) => !['selected', 'disabled'].includes(prop),
})(({ theme, selected, disabled }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    height: '108px',
    overflow: 'hidden',
    backgroundColor: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.14)'
      : selected
        ? (theme.palette.mode === 'dark' ? 'rgba(3, 133, 255, 0.18)' : 'rgba(3, 133, 255, 0.10)')
        : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#F5F6FA'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    borderRadius: '20px',
    padding: '14px 16px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    opacity: disabled ? 0.6 : 1,
    border: selected
      ? isWallpaperMode
        ? '2px solid rgba(255, 255, 255, 0.8)'
        : 'none'
      : isWallpaperMode
        ? `1px solid rgba(255, 255, 255, 0.25)`
        : 'none',
    '&:hover': {
      backgroundColor: disabled
        ? undefined
        : isWallpaperMode
          ? 'rgba(255, 255, 255, 0.14)'
          : selected
            ? (theme.palette.mode === 'dark' ? 'rgba(3, 133, 255, 0.22)' : 'rgba(3, 133, 255, 0.14)')
            : (theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.04)'
              : 'rgba(3, 133, 255, 0.04)'),
      transform: disabled ? 'none' : 'translateY(-1px)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 16,
      left: 12,
      width: '6px',
      height: '28px',
      borderRadius: '99px',
      background: selected
        ? ACCENT_COLOR
        : isWallpaperMode
          ? 'rgba(255, 255, 255, 0.6)'
          : ACCENT_COLOR,
      opacity: disabled ? 0.4 : 1,
    },
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: '8px 12px',
      height: '96px',
    }
  };
});

// Estilo para el icono circular
const PromptTextStack = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  paddingLeft: '14px',
  paddingRight: '26px',
  paddingBottom: '6px',
  paddingTop: '4px',
}));

// Estilo para el texto del prompt
const PromptText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  fontSize: '14.5px',
  fontWeight: selected ? 600 : 600,
  color: theme.palette.text.primary,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
  }
}));

const PromptSubtext = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.6)'
    : 'rgba(0, 0, 0, 0.55)',
  lineHeight: 1.35,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    fontSize: '11.5px',
    WebkitLineClamp: 1,
  }
}));

// Estilo para el icono de verificación
const CheckmarkIcon = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'absolute',
    top: 10,
    right: 10,
    width: '22px',
    height: '22px',
    borderRadius: '8px',
    backgroundColor: isWallpaperMode ? '#FFFFFF' : ACCENT_COLOR,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {
      color: isWallpaperMode ? '#000000' : '#FFFFFF',
      width: '12px',
      height: '12px',
    },
    [theme.breakpoints.down('sm')]: {
      width: '20px',
      height: '20px',
      '& svg': {
        width: '12px',
        height: '12px',
      }
    }
  };
});

const PromptCard = React.memo(({
  text,
  subtext,
  isProOnly,
  isPro,
  onClick,
  selected
}) => {
  const { t } = useTranslation();
  const isDisabled = isProOnly && !isPro;

  const handleClick = (e) => {
    if (!isDisabled) {
      onClick(e);
    }
  };

  return (
    <Tooltip
      title={isDisabled ? t('homeInput.proFeature') : ''}
      placement="bottom"
      arrow
    >
      <PromptCardItem
        onClick={handleClick}
        disabled={isDisabled}
        selected={selected}
      >
        <PromptTextStack>
          <PromptText selected={selected}>
            {text}
          </PromptText>
          {subtext && <PromptSubtext>{subtext}</PromptSubtext>}
        </PromptTextStack>
        {selected && (
          <CheckmarkIcon>
            <Check />
          </CheckmarkIcon>
        )}
      </PromptCardItem>
    </Tooltip>
  );
});

const PromptCards = ({ isPro = false, onSelectPrompt, selectedPrompt, userId = null }) => {
  const { t } = useTranslation();
  const [onboardingContext, setOnboardingContext] = useState(null);

  useEffect(() => {
    let isMounted = true;

    if (!userId) {
      setOnboardingContext(null);
      return () => {
        isMounted = false;
      };
    }

    const loadOnboardingContext = async () => {
      try {
        const response = await getUserOnboardingData(userId);
        if (!isMounted) return;
        setOnboardingContext(normalizeOnboardingContext(response?.onboardingData));
      } catch (error) {
        if (!isMounted) return;
        setOnboardingContext(null);
      }
    };

    loadOnboardingContext();

    return () => {
      isMounted = false;
    };
  }, [userId]);
  
  const cards = useMemo(() => {
    const handleCardClick = (action) => {
      onSelectPrompt(action);
    };

    const adaptivePromptCards = buildAdaptivePromptCards(t, onboardingContext);
    
    return adaptivePromptCards.map((card, index) => (
      <Grow 
        key={card.text}
        in={true} 
        timeout={(index + 1) * 200}
      >
        <Box>
          <PromptCard
            text={card.text}
            subtext={card.subtext}
            action={card.action}
            isProOnly={Boolean(card.isProOnly)}
            isPro={isPro}
            onClick={() => handleCardClick({ type: card.action, prompt: card.prompt })}
            selected={selectedPrompt === card.action}
          />
        </Box>
      </Grow>
    ));
  }, [isPro, onboardingContext, onSelectPrompt, selectedPrompt, t]);

  return (
    <PromptCardsContainer>
      {cards}
    </PromptCardsContainer>
  );
};

PromptCard.propTypes = {
  text: PropTypes.string.isRequired,
  subtext: PropTypes.string,
  isProOnly: PropTypes.bool.isRequired,
  isPro: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  selected: PropTypes.bool,
};

PromptCards.propTypes = {
  onSelectPrompt: PropTypes.func.isRequired,
  isPro: PropTypes.bool,
  selectedPrompt: PropTypes.string,
  userId: PropTypes.string,
};

export default React.memo(PromptCards);
